import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, Tab, Tabs, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import dayjs from "utils/dayjs";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { Avatar, DataTable, SelectFilter, TextField } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { CardEnrollment, EnrolledCard } from "./types";
import { matchesCardSearch } from "./cardEnrollment";
import CardNetworkMark from "./CardNetworkMark";
import CardStatusBadge from "./CardStatusBadge";

const TABS: {
  enrollment: CardEnrollment;
  labelKey: string;
  emptyKey: string;
}[] = [
  {
    enrollment: "enrolled",
    labelKey: "EnrolledCardsTab",
    emptyKey: "NoCardsForConnection",
  },
  {
    enrollment: "unassigned",
    labelKey: "UnassignedCardsTab",
    emptyKey: "No unassigned cards",
  },
  {
    enrollment: "unenrolled",
    labelKey: "UnenrolledCardsTab",
    emptyKey: "No unenrolled cards",
  },
];

// Tabs, toolbar, header and about four rows; below this the page scrolls instead. See README.md.
const MIN_SECTION_HEIGHT = 420;

export interface EnrolledCardsTableProps {
  cards: EnrolledCard[];
  unAssignedCards: EnrolledCard[];
  loading: boolean;
  onSelectCard: (cardId: string) => void;
}

/** Every imported card, split by whether it is enrolled and assigned. See README.md. */
const EnrolledCardsTable: React.FC<EnrolledCardsTableProps> = ({
  cards,
  unAssignedCards,
  loading,
  onSelectCard,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [programIds, setProgramIds] = useState<string[]>([]);

  const tab = TABS[activeTab];

  // Includes pending cards, so a program whose cards are all unassigned can still be filtered.
  const programOptions = useMemo(() => {
    const programs = new Map<string, string>();
    [...cards, ...unAssignedCards].forEach((card) =>
      programs.set(card.connectionId, card.connectionName),
    );
    return Array.from(programs, ([value, label]) => ({ value, label }));
  }, [cards, unAssignedCards]);

  // Drops a selection whose program has left the list, e.g. after a disconnect.
  const activeProgramIds = useMemo(
    () =>
      programIds.filter((id) =>
        programOptions.some((option) => option.value === id),
      ),
    [programIds, programOptions],
  );

  // Counts ignore the search and filter so the tabs stay stable while typing.
  const tabCounts = useMemo(() => {
    const counts: Record<CardEnrollment, number> = {
      enrolled: 0,
      unassigned: unAssignedCards?.length || 0,
      unenrolled: 0,
    };
    cards.forEach((card) => {
      if (card.enrollment !== "unassigned") {
        counts[card.enrollment] += 1;
      }
    });
    return counts;
  }, [cards, unAssignedCards]);

  const rows = useMemo(() => {
    const tabCards =
      tab.enrollment === "unassigned"
        ? unAssignedCards
        : cards.filter((card) => card.enrollment === tab.enrollment);
    return tabCards.filter(
      (card) =>
        (activeProgramIds.length === 0 ||
          activeProgramIds.includes(card.connectionId)) &&
        matchesCardSearch(card, searchTerm),
    );
  }, [unAssignedCards, cards, tab.enrollment, activeProgramIds, searchTerm]);

  const filterLabels = useMemo(
    () => ({
      search: (label: string) =>
        t("SelectFilterSearch", { label: label.toLowerCase() }),
      noMatches: (label: string, query: string) =>
        t("SelectFilterNoMatches", { label: label.toLowerCase(), query }),
      available: (total: number) => t("SelectFilterAvailable", { total }),
    }),
    [t],
  );

  const columns = useMemo<DataColumn<EnrolledCard>[]>(() => {
    const all: DataColumn<EnrolledCard>[] = [
      {
        id: "nameOnCard",
        header: t("Name on Card"),
        cell: (row) => (
          <Inline gap="var(--space-3)" align="center" wrap={false}>
            <Avatar size="sm" name={row.cardholderName} />
            <span>{row.cardholderName}</span>
          </Inline>
        ),
      },
      {
        id: "program",
        header: t("Program"),
        cell: (row) => (
          <Inline gap="var(--space-3)" align="center" wrap={false}>
            <CardNetworkMark name={row.connectionNetwork} />
            <span>{row.connectionName}</span>
          </Inline>
        ),
      },
      {
        id: "card",
        header: t("Card"),
        width: 130,
        // cardNumber is already masked to the last four by the server.
        cell: (row) => `•••• ${row.cardNumber}`,
      },
      {
        id: "enrolled",
        header: t("Enrolled Date"),
        width: 140,
        // Null for every Astrada card today: nothing sets an activation date on them yet.
        cell: (row) =>
          row.activatedAt ? dayjs(row.activatedAt).format("MM/DD/YYYY") : "-",
      },
      {
        id: "status",
        header: t("Status"),
        width: 150,
        cell: (row) => (
          <CardStatusBadge enrollment={row.enrollment} status={row.status} />
        ),
      },
      {
        id: "assignedUser",
        header: t("Assigned User"),
        cell: (row) => row.assignedUser?.name || "-",
      },
    ];

    // An unassigned card has no user by definition, so the column would be all dashes.
    return tab.enrollment === "unassigned"
      ? all.filter((column) => column.id !== "assignedUser")
      : all;
  }, [t, tab.enrollment]);

  const emptyMessage =
    searchTerm.trim() !== ""
      ? t("No cards match your search")
      : activeProgramIds.length > 0
        ? t("No cards in the selected programs")
        : t(tab.emptyKey);

  return (
    <Stack
      gap="var(--space-3)"
      sx={{ flex: "1 1 0", minHeight: MIN_SECTION_HEIGHT }}
    >
      <Typography variant="h6">{t("Cards")}</Typography>

      <Card
        variant="outlined"
        sx={{
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          padding: 0,
          gap: 0,
          overflow: "hidden",
          flex: "1 1 auto",
          minHeight: 0,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_event, next: number) => setActiveTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label={t("Cards")}
        >
          {TABS.map(({ enrollment, labelKey }) => (
            <Tab
              key={enrollment}
              label={t(labelKey, { count: tabCounts[enrollment] })}
            />
          ))}
        </Tabs>

        <Box
          sx={{
            padding: "var(--space-4) var(--space-5)",
            borderTop: "var(--border-width-1) solid var(--color-border)",
            borderBottom: "var(--border-width-1) solid var(--color-border)",
          }}
        >
          <Inline align="center" gap="var(--space-3)" wrap>
            <Box sx={{ flex: 1, maxWidth: 360, minWidth: 0 }}>
              <TextField
                search
                fullWidth
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onClear={() => setSearchTerm("")}
                placeholder={t("Search cards")}
                inputProps={{ "aria-label": t("Search cards") }}
                size="md"
              />
            </Box>
            <SelectFilter
              multiple
              label={t("Program")}
              options={programOptions}
              selected={activeProgramIds}
              onChange={setProgramIds}
              labels={filterLabels}
            />
          </Inline>
        </Box>

        <Box sx={{ flex: "1 1 auto", minHeight: 0, display: "flex" }}>
          <DataTable<EnrolledCard>
            columns={columns}
            rows={rows}
            getRowKey={(row) => row.id}
            onRowClick={(row) => onSelectCard(row.id)}
            loading={loading}
            loadingRowCount={3}
            emptyState={emptyMessage}
          />
        </Box>
      </Card>
    </Stack>
  );
};

export default EnrolledCardsTable;
